import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/motion/Reveal";
import { TileImage } from "@/components/TileImage";
import { CATEGORY_LABELS } from "@/data/content";
import { getCategories } from "@/lib/api";

// Fallback statico (usato solo se l'API non risponde), così la sezione non resta mai vuota.
const FALLBACK = Object.entries(CATEGORY_LABELS).map(([key, label]) => ({
  key, label, image: "", image_scale: 1, image_fit: "cover", image_pos_x: 50, image_pos_y: 50,
}));

const item = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] },
  }),
};

// Riga "Le categorie più ricercate". Legge le categorie dal database
// (nome, ordine, visibilità, foto, zoom e posizione) con fallback statico.
// Versione con profondità e micro-animazioni (contenuti e foto invariati).
export function CategoriesRow({ title = "Le categorie più ricercate" }) {
  const [categories, setCategories] = useState(FALLBACK);
  const reduce = useReducedMotion();

  useEffect(() => {
    let alive = true;
    getCategories()
      .then((data) => {
        if (alive && Array.isArray(data) && data.length) setCategories(data);
      })
      .catch(() => {
        /* mantiene il fallback statico */
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section className="section-tight" data-testid="home-categories">
      <div className="container-page">
        <Reveal>
          <h2 className="display-lg font-extrabold text-foreground">{title}</h2>
        </Reveal>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4 lg:gap-6">
          {categories.map((c, i) => (
            <motion.div
              key={c.key}
              custom={i}
              variants={item}
              initial={reduce ? false : "hidden"}
              whileInView={reduce ? undefined : "show"}
              viewport={{ once: true, margin: "-60px" }}
            >
              <Link
                to={`/catalogo?category=${c.key}`}
                className="group relative block focus-visible:outline-none"
                data-testid={`home-category-${c.key}`}
              >
                {/* Alone lime che compare all'hover — profondità/anima (solo opacità) */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -inset-1.5 rounded-[26px] bg-lime/25 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100"
                />

                <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden rounded-2xl bg-lime shadow-[0_8px_24px_-12px_rgba(0,0,0,0.6)] transition-transform duration-300 ease-out will-change-transform group-hover:-translate-y-1.5 group-active:translate-y-0">
                  {/* Foto con zoom morbido all'hover (posizione/zoom base invariati) */}
                  <div className="absolute inset-0 transition-transform duration-[600ms] ease-out will-change-transform group-hover:scale-[1.07]">
                    {c.image ? (
                      <TileImage
                        url={c.image}
                        alt={c.label}
                        scale={c.image_scale}
                        fit={c.image_fit}
                        posX={c.image_pos_x}
                        posY={c.image_pos_y}
                      />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center px-3 text-center text-sm font-bold text-ink/70">
                        {c.label}
                      </span>
                    )}
                  </div>

                  {/* Gloss superiore sottile per dare spessore */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-white/15 to-transparent opacity-50"
                  />
                  {/* Riflesso che scorre all'hover */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 transition-[left,opacity] duration-700 ease-out group-hover:left-[110%] group-hover:opacity-100"
                  />
                  {/* Ring interno per bordo definito (profondità) */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/10"
                  />
                </div>

                <span className="mt-3 block text-center text-sm font-bold text-foreground transition-colors duration-200 group-hover:text-lime sm:text-base">
                  {c.label}
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
